import { gql } from '@apollo/client';

export const SUPERADMIN_ORGANISATION_FRAGMENT = gql`
  fragment SuperadminOrganisation on Organisation {
    id
    name
    createdAt
    stats {
      totalPointCount
      totalUsers
      totalProjects
    }
    subscription {
      type
      scheduledType
      scheduledAt
      paused
      pausedAt
      startDate
      endDate
      projectUnitPrice
      subscriptionPrice
      customLimits {
        monthlyCredits
        projectUnitPrice
        subscriptionPrice
      }
    }
    credits {
      balance
      pending
      available
      nextExpiry
      monthlyAllowance {
        total
        remaining
        renewsAt
      }
    }
    odoo {
      leadId
      partnerId
      leadUrl
      partnerUrl
      orderId
      orderName
      orderUrl
      orderOrigin
      orderState
      orderConfirmed
      orderEndsAt
      orderCloseReason
      orderSyncedAt
      orderLinkStatus
      orderCandidates {
        id
        url
      }
    }
    referral {
      role
      firstCutBasisPoints
      recurringCutBasisPoints
      customersCanCancel
      payoutCadence
      minPayoutCents
      hasToken
      referredById
      referredByName
      referredAt
      acquiredBy {
        organisationId
        name
        at
      }
      managedBy {
        organisationId
        name
        at
      }
      billedTo {
        organisationId
        name
        at
      }
    }
  }
`;

export const CREDIT_LEDGER_ENTRY_FRAGMENT = gql`
  fragment CreditLedgerEntryFields on CreditLedgerEntry {
    id
    organisationId
    type
    source
    subscriptionType
    amount
    remaining
    expiresAt
    subscriptionId
    invoice
    note
    grantedBy
    grantedByAccount {
      name
      isSuperAdmin
    }
    effectiveAt
    supersededAt
    cancelledAt
    createdAt
    unitPriceCents
    totalPriceCents
    currency
  }
`;

// The Odoo draft invoice is internal: only the superadmin history asks for it.
export const SUPERADMIN_CREDIT_LEDGER_ENTRY_FRAGMENT = gql`
  ${CREDIT_LEDGER_ENTRY_FRAGMENT}
  fragment SuperadminCreditLedgerEntryFields on CreditLedgerEntry {
    ...CreditLedgerEntryFields
    odooInvoiceId
    odooInvoiceUrl
    invoiceVia
    odooOrderId
    odooOrderUrl
  }
`;

export const SUPERADMIN_SET_ODOO_PARTNER = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  mutation superadminSetOdooPartner($organisationId: ID!, $partnerId: Int) {
    superadminSetOdooPartner(organisationId: $organisationId, partnerId: $partnerId) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_CREATE_ODOO_PARTNER = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  mutation superadminCreateOdooPartner($organisationId: ID!) {
    superadminCreateOdooPartner(organisationId: $organisationId) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_SET_ODOO_ORDER = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  mutation superadminSetOdooOrder($organisationId: ID!, $orderId: Int) {
    superadminSetOdooOrder(organisationId: $organisationId, orderId: $orderId) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_CREATE_ODOO_INVOICE = gql`
  ${SUPERADMIN_CREDIT_LEDGER_ENTRY_FRAGMENT}
  mutation superadminCreateOdooInvoice($entryId: ID!) {
    superadminCreateOdooInvoice(entryId: $entryId) {
      ...SuperadminCreditLedgerEntryFields
    }
  }
`;

export const SUPERADMIN_REFRESH_INVOICES = gql`
  mutation superadminRefreshInvoices($organisationId: ID!) {
    superadminRefreshInvoices(organisationId: $organisationId)
  }
`;

export const SUPERADMIN_CREDIT_LEDGER = gql`
  ${SUPERADMIN_CREDIT_LEDGER_ENTRY_FRAGMENT}
  query superadminCreditLedger($organisationId: ID!, $pagination: CursorPaginationInput) {
    superadminCreditLedger(organisationId: $organisationId, pagination: $pagination) {
      data {
        ...SuperadminCreditLedgerEntryFields
        # Asks Odoo (once for the page): only the histories select it, not every mutation that returns a row.
        sentInvoiceNumber
        paymentStatus
      }
      nextCursor
      total
      hasMore
    }
  }
`;

// The whole ledger, the charges for processed projects too, with what each charge was for.
export const SUPERADMIN_FULL_CREDIT_LEDGER = gql`
  ${SUPERADMIN_CREDIT_LEDGER_ENTRY_FRAGMENT}
  query superadminFullCreditLedger($organisationId: ID!, $pagination: CursorPaginationInput) {
    superadminCreditLedger(organisationId: $organisationId, pagination: $pagination, includeCharges: true) {
      data {
        ...SuperadminCreditLedgerEntryFields
        sentInvoiceNumber
        paymentStatus
        projectId
        projectName
        taskId
        pointCountTotal
        shortfall
      }
      nextCursor
      total
      hasMore
    }
  }
`;

export const SUPERADMIN_LIVE_GRANTS = gql`
  ${CREDIT_LEDGER_ENTRY_FRAGMENT}
  query superadminLiveGrants($organisationId: ID!) {
    superadminLiveGrants(organisationId: $organisationId) {
      ...CreditLedgerEntryFields
    }
  }
`;

export const SUPERADMIN_REMOVE_CREDITS = gql`
  ${CREDIT_LEDGER_ENTRY_FRAGMENT}
  mutation superadminRemoveCredits($entryId: ID!, $amount: Int!) {
    superadminRemoveCredits(entryId: $entryId, amount: $amount) {
      ...CreditLedgerEntryFields
    }
  }
`;

export const SUPERADMIN_GRANT_CREDITS = gql`
  ${SUPERADMIN_CREDIT_LEDGER_ENTRY_FRAGMENT}
  mutation superadminGrantCredits($organisationId: ID!, $amount: Int!, $expiresAt: Date, $note: String, $unitPriceCents: Int) {
    superadminGrantCredits(organisationId: $organisationId, amount: $amount, expiresAt: $expiresAt, note: $note, unitPriceCents: $unitPriceCents) {
      ...SuperadminCreditLedgerEntryFields
    }
  }
`;

export const SUPERADMIN_ORGANISATION_CREATE = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  mutation superadminOrganisationCreate($name: String!, $emails: [String!]!) {
    superadminOrganisationCreate(name: $name, emails: $emails) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_ORGANISATIONS = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  query superadminOrganisations($pagination: CursorPaginationInput, $filters: InputSuperadminOrganisationFilters) {
    superadminOrganisations(pagination: $pagination, filters: $filters) {
      data {
        ...SuperadminOrganisation
      }
      nextCursor
      total
      hasMore
    }
  }
`;

// One organisation as the admin center shows it, fresh: its card in the list is updated in place.
export const SUPERADMIN_ORGANISATION_BY_ID = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  query superadminOrganisationById($organisationId: ID!) {
    organisationById(organisationId: $organisationId) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_ORGANISATIONS_PER_MONTH = gql`
  query superadminOrganisationsPerMonth($filters: InputSuperadminOrganisationFilters) {
    superadminOrganisationsPerMonth(filters: $filters) {
      month
      count
    }
  }
`;

export const PLAN_TERM_STATE_FRAGMENT = gql`
  fragment PlanTermStateFields on PlanTermState {
    subscriptionType
    scheduledType
    scheduledAt
    active
    pausedAt
    monthlyCredits
    yearlyPriceCents
    projectUnitPriceCents
    startDate
    endDate
    nextRenewal
    monthsTotal
    monthsElapsed
    monthsLeft
  }
`;

export const SUPERADMIN_PLAN_CHANGE_PREVIEW = gql`
  ${PLAN_TERM_STATE_FRAGMENT}
  query superadminPlanChangePreview($organisationId: ID!, $subscriptionType: SubscriptionType!, $effectiveAt: Date, $endDate: Date, $chargeCents: Int, $customLimits: InputSubscriptionCustomLimits, $force: Boolean) {
    superadminPlanChangePreview(organisationId: $organisationId, subscriptionType: $subscriptionType, effectiveAt: $effectiveAt, endDate: $endDate, chargeCents: $chargeCents, customLimits: $customLimits, force: $force) {
      kind
      subscriptionType
      effectiveAt
      scheduled
      alreadyScheduled
      endDate
      minEffectiveAt
      maxEffectiveAt
      changeMonth
      changePeriodStart
      changePeriodEnd
      current {
        ...PlanTermStateFields
      }
      after {
        ...PlanTermStateFields
      }
      next {
        ...PlanTermStateFields
      }
      lines {
        subscriptionType
        from
        to
        months
        monthlyPriceCents
        totalCents
      }
      totalValueCents
      currentTermValueCents
      alreadyPaidCents
      dueCents
      balanceCents
      chargedCents
      overridden
      priceLocked
      extraCredits
      extraCreditsAt
      extraCreditsExpireAt
      timeline {
        from
        to
        subscriptionType
        monthlyCredits
        extraCredits
        month
      }
      invoiceVia
      voidsCurrentTerm
      keepsPending
      supersedesPending
      problems
    }
  }
`;

export const SUPERADMIN_UPDATE_PLAN = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  mutation superadminUpdatePlan($organisationId: ID!, $customLimits: InputSubscriptionCustomLimits!, $subscriptionType: SubscriptionType!, $effectiveAt: Date, $endDate: Date, $chargeCents: Int, $force: Boolean) {
    superadminUpdatePlan(organisationId: $organisationId, customLimits: $customLimits, subscriptionType: $subscriptionType, effectiveAt: $effectiveAt, endDate: $endDate, chargeCents: $chargeCents, force: $force) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_BILLING_DATE_PREVIEW = gql`
  query superadminBillingDatePreview($organisationId: ID!, $renewalDate: Date!, $chargeCents: Int) {
    superadminBillingDatePreview(organisationId: $organisationId, renewalDate: $renewalDate, chargeCents: $chargeCents) {
      renewalDate
      endDate
      currentEndDate
      subscriptionType
      monthsAdded
      monthlyPriceCents
      dueCents
      chargedCents
      overridden
      priceLocked
      odooLinked
      movesPendingChange
      problems
    }
  }
`;

export const SUPERADMIN_SET_BILLING_DATE = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  mutation superadminSetBillingDate($organisationId: ID!, $renewalDate: Date!, $chargeCents: Int) {
    superadminSetBillingDate(organisationId: $organisationId, renewalDate: $renewalDate, chargeCents: $chargeCents) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_PAUSE_SUBSCRIPTION = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  mutation superadminPauseSubscription($organisationId: ID!, $paused: Boolean!) {
    superadminPauseSubscription(organisationId: $organisationId, paused: $paused) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_CANCEL_SUBSCRIPTION = gql`
  ${SUPERADMIN_ORGANISATION_FRAGMENT}
  mutation superadminCancelSubscription($organisationId: ID!, $immediately: Boolean) {
    superadminCancelSubscription(organisationId: $organisationId, immediately: $immediately) {
      ...SuperadminOrganisation
    }
  }
`;

export const SUPERADMIN_FAILED_POINT_CLOUDS = gql`
  query superadminFailedPointClouds($status: FailedPointCloudStatus, $pagination: CursorPaginationInput) {
    superadminFailedPointClouds(status: $status, pagination: $pagination) {
      data {
        id
        projectId
        organisationId
        projectName
        organisationName
        cloudName
        displayName
        fileNames
        sizeBytes
        status
        failureCount
        firstFailedAt
        lastFailedAt
        lastJobId
        lastError
        resolvedAt
        deletedAt
      }
      nextCursor
      total
      hasMore
    }
  }
`;

export const SUPERADMIN_FAILED_POINT_CLOUD_COUNTS = gql`
  query superadminFailedPointCloudCounts {
    superadminFailedPointCloudCounts {
      failed
      resolved
      deleted
    }
  }
`;

export const SUPERADMIN_INVOICES = gql`
  ${SUPERADMIN_CREDIT_LEDGER_ENTRY_FRAGMENT}
  query superadminInvoices($status: InvoiceTrackingStatus, $pagination: CursorPaginationInput) {
    superadminInvoices(status: $status, pagination: $pagination) {
      data {
        status
        organisation {
          id
          name
        }
        entry {
          ...SuperadminCreditLedgerEntryFields
          odooInvoiceNumber
        }
      }
      nextCursor
      total
      hasMore
    }
  }
`;

export const SUPERADMIN_INVOICE_COUNTS = gql`
  query superadminInvoiceCounts {
    superadminInvoiceCounts {
      noInvoice
      draft
      unpaid
      paid
      cancelled
    }
  }
`;

export const SUPERADMIN_PROJECT_SOURCE_FILES = gql`
  query superadminProjectSourceFiles($projectId: ID!) {
    superadminProjectSourceFiles(projectId: $projectId) {
      name
      sizeBytes
      url
    }
  }
`;
